import { submitExpertWaitlist } from './supabaseClient';
/**
 * @component ExpertOnboarding (SipLogic Expert Application)
 * @version 1.0.0
 * @platform SipLogic · siplogic.io
 * @purpose Expert-facing landing page for sommeliers and certified spirits professionals
 */

import { useState, useEffect, useRef, CSSProperties } from "react";

const T = {
  burgundy:        "#4A0E0E",
  burgundyMid:     "#6B1A1A",
  burgundyLight:   "#8B2A2A",
  charcoal:        "#1C1410",
  charcoalMid:     "#2A1E18",
  charcoalLight:   "#3A2A22",
  cream:           "#F5F0E8",
  creamMuted:      "#C8B89A",
  copper:          "#8B6B4A",
  copperLight:     "#B08A60",
  ink:             "#0A0806",
  surface:         "#120E0A",
  surfaceRaised:   "#1A1410",
  gold:            "#C8A84B",
  goldMuted:       "#9A7A35",
} as const;

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Crimson+Pro:ital,wght@0,200;0,300;0,400;1,200;1,300&family=Josefin+Sans:wght@100;200;300&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body {
    background: #0A0806;
    color: #F5F0E8;
    font-family: 'Crimson Pro', Georgia, serif;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }
  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-track { background: #0A0806; }
  ::-webkit-scrollbar-thumb { background: #4A0E0E; }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes expandIn {
    from { opacity: 0; transform: translateY(-10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes drawLine {
    from { width: 0; opacity: 0; }
    to   { width: 80px; opacity: 1; }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.35; }
    50% { opacity: 1; }
  }
  @keyframes goldShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes cardShine {
    from { left: -100%; }
    to   { left:  200%; }
  }
  .sl-nav-link {
    position: relative;
    font-family: 'Josefin Sans', sans-serif;
    font-weight: 200;
    font-size: 10px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: #8B6B4A;
    cursor: pointer;
    padding: 6px 0;
    background: none;
    border: none;
    transition: color 0.4s ease;
  }
  .sl-nav-link::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 0; height: 1px;
    background: #B08A60;
    transition: width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .sl-nav-link:hover { color: #C8B89A; }
  .sl-nav-link:hover::after { width: 100%; }
  .sl-cert-card {
    position: relative;
    background: #120E0A;
    border: 1px solid #251810;
    border-radius: 1px;
    padding: 28px 26px;
    cursor: pointer;
    overflow: hidden;
    transition: background 0.35s ease, border-color 0.35s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.35s ease;
  }
  .sl-cert-card::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(200,168,75,0.06), transparent);
  }
  .sl-cert-card:hover::before { animation: cardShine 0.7s ease forwards; }
  .sl-cert-card:hover { background: #1A1410; border-color: #3A2A18; transform: translateY(-2px); box-shadow: 0 14px 44px rgba(0,0,0,0.55); }
  .sl-cert-card.selected { background: #140E06; border-color: #9A7A35; transform: translateY(-1px); box-shadow: 0 8px 32px rgba(200,168,75,0.15); }
  .sl-cert-card .card-accent { position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: transparent; transition: background 0.4s ease; }
  .sl-cert-card.selected .card-accent { background: linear-gradient(90deg, transparent, #9A7A35, transparent); }
  .sl-input {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1px solid #2A1A14;
    color: #F5F0E8;
    font-family: 'Crimson Pro', Georgia, serif;
    font-size: 16px;
    font-weight: 300;
    padding: 10px 0;
    outline: none;
    caret-color: #B08A60;
    transition: border-color 0.35s ease;
  }
  .sl-input::placeholder { color: #4A3020; font-style: italic; }
  .sl-input:focus { border-bottom-color: #8B6B4A; }
  .sl-input.has-error { border-bottom-color: #8B2A2A; }
  .sl-select {
    width: 100%;
    background: #120E0A;
    border: none;
    border-bottom: 1px solid #2A1A14;
    color: #F5F0E8;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    font-weight: 200;
    letter-spacing: 0.15em;
    padding: 10px 0;
    outline: none;
    cursor: pointer;
    appearance: none;
    transition: border-color 0.35s ease;
  }
  .sl-select:focus { border-bottom-color: #8B6B4A; }
  .sl-select option { background: #1A1008; }
  .sl-cta-gold {
    position: relative;
    overflow: hidden;
    width: 100%;
    background: #9A7A35;
    border: 1px solid #C8A84B;
    color: #0A0806;
    font-family: 'Josefin Sans', sans-serif;
    font-weight: 300;
    font-size: 11px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 17px 48px;
    cursor: pointer;
    transition: background 0.4s ease, transform 0.2s ease;
  }
  .sl-cta-gold::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transition: left 0.55s ease;
  }
  .sl-cta-gold:hover::before { left: 100%; }
  .sl-cta-gold:hover { background: #C8A84B; transform: translateY(-1px); }
  .sl-cta-gold:disabled { opacity: 0.38; cursor: not-allowed; transform: none; }
  .sl-gold-shimmer {
    background: linear-gradient(90deg, #9A7A35 0%, #C8A84B 40%, #B08A60 60%, #9A7A35 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: goldShimmer 5s linear infinite;
  }
  .anim-fade-up  { animation: fadeUp   0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
  .anim-fade-in  { animation: fadeIn   0.6s ease both; }
  .anim-expand   { animation: expandIn 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
`;

type CertLevel = "introductory" | "certified" | "advanced" | "master";

interface CertTier {
  id: CertLevel;
  icon: string;
  label: string;
  sub: string;
  rate: string;
  rateNote: string;
  desc: string;
}

const CERT_TIERS: CertTier[] = [
  { id: "introductory", icon: "◇", label: "Introductory", sub: "WSET 2 · CMS Level 1 · CSW Candidate", rate: "$95", rateNote: "per event", desc: "Entry-level certified professionals with foundational wine and spirits knowledge. Ideal for high-volume retail tastings." },
  { id: "certified", icon: "◈", label: "Certified Sommelier", sub: "CMS Level 2 · WSET 3 · CSW", rate: "$175", rateNote: "per event", desc: "Mid-level professionals with 3–7 years experience. The workhorse of the platform — credentialed, articulate, sales-focused." },
  { id: "advanced", icon: "◉", label: "Advanced Sommelier", sub: "CMS Level 3 · WSET Diploma · CSS", rate: "$300", rateNote: "per event", desc: "Senior professionals commanding the room. Preferred by fine wine producers and premium spirits brands." },
  { id: "master", icon: "✦", label: "Master Sommelier", sub: "MS · MW · WSET Level 4", rate: "$550", rateNote: "per event", desc: "Fewer than 300 in the world. When your brand demands the highest credential in the room." },
];

const SPECIALTIES = ["Bordeaux & Burgundy","New World Wine","American Whiskey","Scotch & Irish Whisky","Tequila & Mezcal","Gin & Vodka","Rum & Cachaça","Sparkling Wine","Fortified Wine","Sake & Japanese Spirits","Craft Beer & Cider","Food & Wine Pairing"];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function OrnamentRule() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #3A2A22)" }} />
      <span style={{ color: "#9A7A35", fontSize: 11, opacity: 0.7 }}>✦</span>
      <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #3A2A22, transparent)" }} />
    </div>
  );
}

function Eyebrow({ children, gold }: { children: React.ReactNode; gold?: boolean }) {
  return (
    <p style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.5em", textTransform: "uppercase", color: gold ? "#9A7A35" : "#8B6B4A", marginBottom: 16 }}>
      {children}
    </p>
  );
}

function EarningCard({ tier }: { tier: CertTier }) {
  return (
    <div style={{ padding: "20px 22px", background: "#120E0A", border: "1px solid #251810", textAlign: "center" }}>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 11, marginBottom: 2, color: tier.id === "master" ? "#C8A84B" : "#8B6B4A" }}>{tier.icon}</div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 12, color: "#C8B89A", letterSpacing: "0.04em", marginBottom: 8 }}>{tier.label}</div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 30, color: tier.id === "master" ? "#C8A84B" : "#F5F0E8", lineHeight: 1 }}>{tier.rate}</div>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", color: "#8B6B4A", marginTop: 4 }}>{tier.rateNote}</div>
    </div>
  );
}

interface ExpertForm { name: string; email: string; phone: string; state: string; certification: string; certBody: string; yearsExp: string; bio: string; }
type FormErrors = Partial<Record<keyof ExpertForm, string>>;

function NavCTAButton() {
  const [hovered, setHovered] = useState(false);
  return (
    <button onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#0A0806", background: hovered ? "#C8A84B" : "#9A7A35", border: "1px solid #C8A84B", padding: "8px 20px", cursor: "pointer", transition: "all 0.35s ease" }}>
      Apply Now
    </button>
  );
}

export default function ExpertPage() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedCert, setSelectedCert] = useState<CertLevel | null>(null);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [form, setForm] = useState<ExpertForm>({ name: "", email: "", phone: "", state: "", certification: "", certBody: "", yearsExp: "", bio: "" });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const formRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const tag = document.createElement("style");
    tag.innerHTML = GLOBAL_CSS;
    document.head.appendChild(tag);
    return () => { document.head.removeChild(tag); };
  }, []);

  useEffect(() => {
    const handler = () => setNavScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.state) e.state = "Required";
    if (!form.certification.trim()) e.certification = "Required";
    if (!form.yearsExp) e.yearsExp = "Required";
    return e;
  };

  const onChange = (key: keyof ExpertForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const toggleSpecialty = (s: string) => setSelectedSpecialties(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const handleCertSelect = (id: CertLevel) => {
    setSelectedCert(id);
    setTimeout(() => { setStep(2); formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 280);
  };
const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      await submitExpertWaitlist({
        full_name: form.name,
        email: form.email,
        phone: form.phone,
        state: form.state,
        certification_level: selectedCert || '',
        certification_body: form.certification,
        years_experience: form.yearsExp,
        specialties: selectedSpecialties,
        bio: form.bio,
      });
      setStep(3);
    } catch (err) {
      console.error(err);
      setStep(3);
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = (hasError?: boolean): CSSProperties => ({ display: "block", fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.4em", textTransform: "uppercase", color: hasError ? "#8B2A2A" : "#8B6B4A", marginBottom: 8 });
  const selectedTierData = CERT_TIERS.find(t => t.id === selectedCert);

  return (
    <div style={{ minHeight: "100vh", background: "#0A0806" }}>
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, padding: "0 48px", background: navScrolled ? "linear-gradient(180deg,rgba(10,6,4,0.97),rgba(10,6,4,0.92))" : "linear-gradient(180deg,rgba(10,6,4,0.75),transparent)", backdropFilter: navScrolled ? "blur(14px)" : "none", borderBottom: `1px solid ${navScrolled ? "rgba(200,168,75,0.2)" : "transparent"}`, transition: "background 0.55s ease" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", height: 72, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 23, color: "#F5F0E8", letterSpacing: "0.08em" }}>SipLogic</span>
            <span style={{ width: 1, height: 14, background: "#9A7A35", display: "inline-block" }} />
            <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, color: "#9A7A35", letterSpacing: "0.3em", textTransform: "uppercase" }}>For Experts</span>
          </div>
          <div style={{ display: "flex", gap: 36, alignItems: "center" }}>
            {["How It Works", "Earning Rates", "For Producers"].map(label => <button key={label} className="sl-nav-link">{label}</button>)}
            <NavCTAButton />
          </div>
        </div>
      </nav>

      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "120px 24px 80px", position: "relative", background: "radial-gradient(ellipse 90% 70% at 50% 30%, #0E0A04 0%, #0A0806 70%)", textAlign: "center" }}>
        {[600, 420, 260].map(size => (
          <div key={size} style={{ position: "absolute", top: "35%", left: "50%", transform: "translate(-50%, -50%)", width: size, height: size, borderRadius: "50%", border: `1px solid rgba(200,168,75,${0.04 + (600 - size) / 4000})`, pointerEvents: "none" }} />
        ))}
        <div className="anim-fade-up" style={{ animationDelay: "0.1s", marginBottom: 24 }}>
          <OrnamentRule />
          <div style={{ marginTop: 16 }}><Eyebrow gold>Georgia's Premier Expert Tasting Network</Eyebrow></div>
        </div>
        <h1 className="anim-fade-up" style={{ animationDelay: "0.25s", fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "clamp(44px, 7.5vw, 82px)", color: "#F5F0E8", lineHeight: 1.1, letterSpacing: "0.02em", maxWidth: 800 }}>
          Your Credential.<br /><em className="sl-gold-shimmer" style={{ fontStyle: "italic" }}>Their Bottle.</em>
        </h1>
        <p className="anim-fade-up" style={{ animationDelay: "0.42s", fontFamily: "'Crimson Pro', serif", fontWeight: 300, fontSize: 20, color: "#B08A60", maxWidth: 540, marginTop: 24, lineHeight: 1.75, fontStyle: "italic" }}>
          SipLogic connects certified sommeliers and spirits professionals with Georgia producers who need your expertise at retail — on your schedule, at rates that respect your credential.
        </p>
        <div className="anim-fade-in" style={{ animationDelay: "0.65s", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, marginTop: 56, width: "100%", maxWidth: 680, background: "#1A1008" }}>
          {CERT_TIERS.map(tier => <EarningCard key={tier.id} tier={tier} />)}
        </div>
        <p className="anim-fade-in" style={{ animationDelay: "0.85s", marginTop: 16, fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#3A2A22" }}>
          Per 3–4 hour tasting event · Paid within 48 hours via Stripe
        </p>
        <div className="anim-fade-in" style={{ animationDelay: "1s", display: "flex", gap: 48, marginTop: 56 }}>
          {[{ n: "Your Schedule", label: "Book events when you choose" }, { n: "GA DOR", label: "Compliant by design" }, { n: "48hr Pay", label: "Stripe milestone payments" }].map(stat => (
            <div key={stat.label} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 20, color: "#C8B89A", letterSpacing: "0.06em" }}>{stat.n}</div>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 8, letterSpacing: "0.35em", textTransform: "uppercase", color: "#8B6B4A", marginTop: 4 }}>{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="anim-fade-in" style={{ animationDelay: "1.4s", marginTop: 72, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.4em", textTransform: "uppercase", color: "#3A2A22" }}>Apply Now</span>
          <div style={{ width: 1, height: 52, background: "linear-gradient(180deg, #3A2A22, transparent)", animation: "pulse 2.2s ease-in-out infinite" }} />
        </div>
      </section>

      <section style={{ padding: "80px 24px", background: "linear-gradient(180deg, #0A0806 0%, #0C0906 100%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ width: "100%", maxWidth: 860 }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <Eyebrow gold>The Platform</Eyebrow>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 38, color: "#F5F0E8", letterSpacing: "0.03em", lineHeight: 1.2 }}>How SipLogic works for you.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, background: "#1A1008" }}>
            {[
              { step: "01", icon: "◈", title: "Apply & Verify", body: "Submit your certification credentials. We verify with the Court of Master Sommeliers, WSET, or NABSA within 48 hours. Your tier determines your rate floor — you can always charge more for premium engagements." },
              { step: "02", icon: "◇", title: "Browse & Accept", body: "Producers post tasting events with date, location, product, and duration. You browse open events matching your availability and specialties. Accept what fits your schedule — no minimum commitment required." },
              { step: "03", icon: "◉", title: "Pour & Get Paid", body: "Represent the brand at retail. File your post-event report through the SipLogic app. Payment hits your Stripe account within 48 hours. GA DOR compliance documentation is handled automatically." },
            ].map(item => (
              <div key={item.step} style={{ padding: "32px 28px", background: "#120E0A" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.4em", color: "#9A7A35" }}>{item.step}</span>
                  <span style={{ color: "#9A7A35", fontSize: 14 }}>{item.icon}</span>
                </div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 22, color: "#F5F0E8", letterSpacing: "0.04em", marginBottom: 12 }}>{item.title}</h3>
                <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 16, color: "#B08A60", fontStyle: "italic", lineHeight: 1.75 }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={formRef} style={{ minHeight: "100vh", padding: "80px 24px 140px", background: "linear-gradient(180deg, #0C0906 0%, #0E0A08 100%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {step !== 3 ? (
          <div style={{ width: "100%", maxWidth: 660 }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <Eyebrow gold>Step {step} of 2 — {step === 1 ? "Your Certification" : "Your Profile"}</Eyebrow>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 38, color: "#F5F0E8", letterSpacing: "0.03em", lineHeight: 1.2 }}>
                {step === 1 ? "Select your highest certification." : "Tell us about your expertise."}
              </h2>
              {step === 1 && <p style={{ marginTop: 14, fontFamily: "'Crimson Pro', serif", fontSize: 17, color: "#B08A60", fontStyle: "italic", lineHeight: 1.65 }}>Your certification tier determines your base rate. Select the highest credential you currently hold.</p>}
            </div>

            {step === 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {CERT_TIERS.map(tier => (
                  <div key={tier.id} className={`sl-cert-card${selectedCert === tier.id ? " selected" : ""}`} onClick={() => handleCertSelect(tier.id)}>
                    <div style={{ position: "absolute", top: 16, right: 16, fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 20, color: tier.id === "master" ? "#C8A84B" : "#C8B89A", letterSpacing: "0.04em" }}>
                      {tier.rate}<span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 8, letterSpacing: "0.2em", color: "#8B6B4A", marginLeft: 6 }}>/ event</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
                      <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, lineHeight: 1, marginTop: 2, flexShrink: 0, color: selectedCert === tier.id ? "#C8A84B" : "#9A7A35", transition: "color 0.3s ease" }}>{tier.icon}</span>
                      <div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 5 }}>
                          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 21, color: selectedCert === tier.id ? "#F5F0E8" : "#C8B89A", letterSpacing: "0.04em", transition: "color 0.3s ease" }}>{tier.label}</span>
                          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "#8B6B4A" }}>{tier.sub}</span>
                        </div>
                        <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 15, color: "#B08A60", fontStyle: "italic", lineHeight: 1.65 }}>{tier.desc}</p>
                      </div>
                    </div>
                    <div className="card-accent" />
                  </div>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="anim-expand">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", marginBottom: 32, background: "#120E0A", border: "1px solid #9A7A35" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 20, color: "#C8A84B" }}>{selectedTierData?.icon}</span>
                    <div>
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#9A7A35", marginBottom: 3 }}>Certification Tier — Locked</div>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#F5F0E8" }}>{selectedTierData?.label} · {selectedTierData?.rate}/event</div>
                    </div>
                  </div>
                  <button onClick={() => { setStep(1); setSelectedCert(null); }} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Josefin Sans', sans-serif", fontWeight: 200, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#8B6B4A" }}>Change →</button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 34 }}>
                  {[
                    { key: "name" as const, label: "Your Full Name", placeholder: "Jane Smith MS", type: "text" },
                    { key: "email" as const, label: "Professional Email", placeholder: "jane@yourdomain.com", type: "email" },
                    { key: "phone" as const, label: "Phone (optional)", placeholder: "404 · 000 · 0000", type: "tel" },
                    { key: "certification" as const, label: "Certification Number or Body", placeholder: "CMS #12345 · Court of Master Sommeliers", type: "text" },
                  ].map(({ key, label, placeholder, type }) => (
                    <div key={key}>
                      <label style={labelStyle(!!errors[key])}>{label}{errors[key] && <span style={{ marginLeft: 10, fontStyle: "italic", fontSize: 8, color: "#8B2A2A" }}>— {errors[key]}</span>}</label>
                      <input className={`sl-input${errors[key] ? " has-error" : ""}`} type={type} placeholder={placeholder} value={form[key]} onChange={onChange(key)} autoComplete="off" />
                    </div>
                  ))}

                  <div>
                    <label style={labelStyle(!!errors.yearsExp)}>Years of Industry Experience{errors.yearsExp && <span style={{ marginLeft: 10, fontStyle: "italic", fontSize: 8, color: "#8B2A2A" }}>— Required</span>}</label>
                    <div style={{ position: "relative" }}>
                      <select className="sl-select" value={form.yearsExp} onChange={onChange("yearsExp")}>
                        <option value="">Select range…</option>
                        <option value="1-2">1–2 years</option>
                        <option value="3-5">3–5 years</option>
                        <option value="6-10">6–10 years</option>
                        <option value="10+">10+ years</option>
                      </select>
                      <span style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", color: "#8B6B4A", fontSize: 10, pointerEvents: "none" }}>▾</span>
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle(!!errors.state)}>Primary Market State{errors.state && <span style={{ marginLeft: 10, fontStyle: "italic", fontSize: 8, color: "#8B2A2A" }}>— Required</span>}</label>
                    <div style={{ position: "relative" }}>
                      <select className="sl-select" value={form.state} onChange={onChange("state")}>
                        <option value="">Select state…</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <span style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", color: "#8B6B4A", fontSize: 10, pointerEvents: "none" }}>▾</span>
                    </div>
                    {form.state && form.state !== "GA" && <p className="anim-fade-in" style={{ marginTop: 10, fontFamily: "'Crimson Pro', serif", fontSize: 14, color: "#B08A60", fontStyle: "italic" }}>SipLogic is launching in Georgia first. We'll contact you personally when we expand to {form.state}.</p>}
                  </div>

                  <div>
                    <label style={labelStyle()}>Specialties <span style={{ color: "#3A2A22" }}>(select all that apply)</span></label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                      {SPECIALTIES.map(s => {
                        const active = selectedSpecialties.includes(s);
                        return (
                          <button key={s} onClick={() => toggleSpecialty(s)} style={{ background: active ? "#120E0A" : "transparent", border: `1px solid ${active ? "#9A7A35" : "#2A1A14"}`, color: active ? "#C8A84B" : "#8B6B4A", fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 8, letterSpacing: "0.25em", textTransform: "uppercase", padding: "6px 12px", cursor: "pointer", transition: "all 0.25s ease" }}>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle()}>Brief Professional Bio <span style={{ color: "#3A2A22" }}>(optional)</span></label>
                    <textarea className="sl-input" placeholder="2–3 sentences about your background, certifications, and what makes you exceptional at retail tastings…" value={form.bio} onChange={onChange("bio")} rows={3} style={{ resize: "none", lineHeight: 1.65 }} />
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <button className="sl-cta-gold" onClick={handleSubmit} disabled={submitting}>
                      {submitting ? <span style={{ animation: "pulse 1s ease-in-out infinite" }}>✦ &nbsp; Verifying credentials…</span> : "Submit Application"}
                    </button>
                    <p style={{ marginTop: 16, textAlign: "center", fontFamily: "'Crimson Pro', serif", fontSize: 13, color: "#3A2A1A", fontStyle: "italic" }}>We review every application personally. You'll hear from us within 48 hours.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="anim-fade-up" style={{ width: "100%", maxWidth: 560, textAlign: "center" }}>
            <div style={{ fontSize: 48, color: "#C8A84B", fontFamily: "'Cormorant Garamond', serif", marginBottom: 28, lineHeight: 1, animation: "pulse 3s ease-in-out infinite" }}>✦</div>
            <div style={{ height: 1, margin: "0 auto 40px", background: "linear-gradient(90deg, transparent, #9A7A35, transparent)", animation: "drawLine 1s ease 0.2s both" }} />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 42, color: "#F5F0E8", letterSpacing: "0.04em", lineHeight: 1.15, marginBottom: 20 }}>Application received.</h2>
            <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 19, color: "#B08A60", fontStyle: "italic", lineHeight: 1.8, maxWidth: 420, margin: "0 auto 40px" }}>
              {form.name || "Your application"} has been submitted. We verify all credentials directly with the issuing body and will confirm your tier and onboarding details within 48 hours.
            </p>
            <div style={{ display: "inline-block", padding: "10px 28px", border: "1px solid #3A2A22", fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.4em", textTransform: "uppercase", color: "#9A7A35" }}>
              {selectedTierData?.label} · {form.state || "GA"} · Pending Verification
            </div>
          </div>
        )}
      </section>

      <footer style={{ borderTop: "1px solid #1A1008", padding: "28px 48px", background: "#0A0806", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 18, color: "#3A2A22", letterSpacing: "0.06em" }}>SipLogic</span>
        <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontWeight: 100, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#2A1A10" }}>siplogic.io · Georgia · For Experts</span>
        <span style={{ fontFamily: "'Crimson Pro', serif", fontSize: 13, color: "#2A1A10", fontStyle: "italic" }}>Where expertise meets the pour.</span>
      </footer>
    </div>
  );
}
